import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { nextFieldFocusHandler } from "../utils/inputHandler";
import { i18_TRANSLATION } from "../../i18nReferences";
import { loginSchema } from "./loginSchema";
import { renderLoginAlert, renderLoginResetAlert } from "./loginAlerts";

/**
 * Artist Login View Component
 * Pure presentation component for artist login with simple centered layout
 */
const LoginArtistView = ({
    t,
    onSubmit,
    isLoginFailed,
    isLoggedIn,
    successText,
    loginFailedText,
    nameText,
    onClickReset,
    loginResetError,
    loginResetResult,
}) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
    } = useForm({
        resolver: yupResolver(loginSchema),
    });
    
    const name = useWatch({ control, name: "name" });

    return (
        (<Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <Box sx={{ maxWidth: "500px", width: "100%", px: 2, pb: 4 }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid
                        container
                        spacing={2}
                        sx={{
                            alignContent: "center",
                            flexDirection: "column"
                        }}>
                        <Grid>
                            <Typography variant="h2" sx={{
                                color: "text.primary"
                            }}>
                                {t("STUDIO_LOGIN.LOGIN_TITLE", { ns: i18_TRANSLATION, namespace: "Artist" })}
                            </Typography>
                        </Grid>
                        <Grid>
                            <TextField
                                autoFocus
                                fullWidth
                                {...register("name")}
                                onKeyUp={nextFieldFocusHandler}
                                error={!!errors?.name}
                                required
                                variant="standard"
                                label={nameText || t("STUDIO_LOGIN.USERNAME_LABEL", { ns: i18_TRANSLATION })}
                            />
                        </Grid>
                        <Grid>
                            <TextField
                                fullWidth
                                {...register("password")}
                                error={!!errors?.password}
                                required
                                type="password"
                                variant="standard"
                                label={t("STUDIO_LOGIN.PASSWORD_LABEL", { ns: i18_TRANSLATION })}
                            />
                        </Grid>
                        <Grid>
                            <Button
                                type="submit"
                                disabled={isLoggedIn}
                                variant="contained"
                                data-testid="login-artist-submit"
                            >
                                {t("STUDIO_LOGIN.CONFIRM_BUTTON", { ns: i18_TRANSLATION })}
                            </Button>
                        </Grid>
                        <Grid sx={{ marginTop: "10px" }}>
                            {renderLoginAlert(isLoginFailed, isLoggedIn, loginFailedText, successText)}
                        </Grid>
                        {name?.length > 2 ? (
                            <Grid sx={{ marginTop: "20px" }}>
                                <Link onClick={() => onClickReset(name)} sx={{ cursor: "pointer" }}>
                                    {t("STUDIO_LOGIN.RESET_PASSWORD", { ns: i18_TRANSLATION })}
                                </Link>
                            </Grid>
                        ) : null}
                        {renderLoginResetAlert(loginResetResult, loginResetError, t, "AUTH.RESET_ARTIST_LOGIN_SUCCESS_INFO")}
                    </Grid>
                </form>
            </Box>
        </Box>)
    );
};

export default LoginArtistView;

